/**
 * ⌘K / Ctrl-K palette.
 *
 * Two jobs. Navigation — 17 modules is more than a sidebar scan, and typing
 * three letters beats hunting a group label. And record lookup: an admin who
 * has a business name or a phone number from a support call currently has to
 * guess which list owns it, open that list, and search there. Here they type
 * the name and get the record.
 *
 * Record search is debounced and only fires past two characters, so opening the
 * palette to jump to a page costs no requests at all.
 */
import { useEffect, useMemo, useState } from 'react';
import { Command } from 'cmdk';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  CalendarCheck2,
  CornerDownLeft,
  Loader2,
  Moon,
  PawPrint,
  Search,
  Store,
  Sun,
  Users,
} from 'lucide-react';
import { api } from '@/lib/api';
import { use_debounced_value } from '@/hooks/use-debounced-value';
import { use_theme_store } from '@/lib/theme-store';
import { use_is_super_admin } from '@/lib/auth-store';
import { NAV_GROUPS } from '@/components/nav-items';
import { date_time, inr, person_name, title_case } from '@/lib/format';

type Hit = { id: string; title: string; subtitle: string; to: string };

/**
 * Three lists in parallel, capped small. The palette is for "find the one I
 * mean", not for browsing — anything past a handful of hits per type means the
 * admin should be on the actual list page with real filters.
 */
async function search_everything(term: string): Promise<{
  vendors: Hit[];
  users: Hit[];
  pets: Hit[];
}> {
  const params = { q: term, page_size: 5 };
  const [vendors, users, pets] = await Promise.all([
    api
      .get<{
        items: {
          id: string;
          business_name: string | null;
          city: string | null;
          kyc_status: string;
        }[];
      }>('/admin/vendors', { params })
      .then((r) => r.data.items)
      .catch(() => []),
    api
      .get<{
        items: {
          id: string;
          first_name: string | null;
          last_name: string | null;
          email: string | null;
          phone: string | null;
          role: string;
        }[];
      }>('/admin/users', { params })
      .then((r) => r.data.items)
      .catch(() => []),
    /*
     * Pets are no longer a module, so this IS the pet search.
     *
     * The API matches name OR microchip id, which is the operationally
     * important one: somebody finds a stray, reads the chip, and needs the
     * owner's phone number. Dropping the module without keeping this would have
     * removed a capability rather than relocating one.
     */
    api
      .get<{
        items: {
          id: string;
          name: string;
          species: string;
          breed_name: string | null;
          is_active: boolean;
          owner: { id: string; name: string | null; phone: string | null };
        }[];
      }>('/admin/pets', { params })
      .then((r) => r.data.items)
      .catch(() => []),
  ]);

  return {
    vendors: vendors.map((v) => ({
      id: v.id,
      title: v.business_name ?? v.id,
      subtitle: [v.city, title_case(v.kyc_status)].filter(Boolean).join(' · '),
      to: `/vendors/${v.id}`,
    })),
    users: users.map((u) => ({
      id: u.id,
      title: person_name(u),
      subtitle: [u.email, u.phone, title_case(u.role)].filter(Boolean).join(' · '),
      to: `/users/${u.id}`,
    })),
    pets: pets.map((p) => ({
      id: p.id,
      title: p.name,
      subtitle: [
        title_case(p.species),
        p.breed_name,
        p.owner.name ?? p.owner.phone,
        p.is_active ? null : 'hidden',
      ]
        .filter(Boolean)
        .join(' · '),
      to: `/users/${p.owner.id}/pets/${p.id}`,
    })),
  };
}

/** Recent bookings matched by nothing but recency — the fallback list. */
async function recent_bookings(): Promise<Hit[]> {
  const res = await api
    .get<{
      items: {
        id: string;
        module_type: string;
        vendor_business_name: string | null;
        price_inr: string;
        slot_start: string;
      }[];
    }>('/admin/bookings', { params: { page_size: 5 } })
    .catch(() => ({ data: { items: [] } }));
  return res.data.items.map((b) => ({
    id: b.id,
    title: `${title_case(b.module_type)} · ${inr(b.price_inr)}`,
    subtitle: [b.vendor_business_name, date_time(b.slot_start)].filter(Boolean).join(' · '),
    to: `/bookings/${b.id}`,
  }));
}

export function CommandPalette() {
  const [open, set_open] = useState(false);
  const [term, set_term] = useState('');
  const navigate = useNavigate();
  const is_super_admin = use_is_super_admin();
  const set_theme = use_theme_store((s) => s.set_choice);
  const debounced = use_debounced_value(term, 220);
  const searching = debounced.trim().length >= 2;

  useEffect(() => {
    const on_key = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        set_open((o) => !o);
      }
    };
    document.addEventListener('keydown', on_key);
    return () => document.removeEventListener('keydown', on_key);
  }, []);

  // Wipe the term on close so reopening isn't haunted by the last search.
  useEffect(() => {
    if (!open) set_term('');
  }, [open]);

  const results = useQuery({
    queryKey: ['palette-search', debounced.trim()],
    queryFn: () => search_everything(debounced.trim()),
    enabled: open && searching,
    staleTime: 30_000,
  });

  const recent = useQuery({
    queryKey: ['palette-recent-bookings'],
    queryFn: recent_bookings,
    enabled: open && !searching,
    staleTime: 60_000,
  });

  const nav_items = useMemo(
    () =>
      NAV_GROUPS.flatMap((g) =>
        g.items
          .filter((i) => !i.hidden && (!i.super_admin_only || is_super_admin))
          .map((i) => ({ ...i, group: g.label })),
      ),
    [is_super_admin],
  );

  function go(to: string) {
    set_open(false);
    navigate(to);
  }

  return (
    <Dialog.Root open={open} onOpenChange={set_open}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-overlay bg-foreground/25 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-[12vh] z-palette w-[min(40rem,calc(100vw-2rem))] -translate-x-1/2 outline-none">
          {/*
            Radix requires a title on any dialog content, and warns in the
            console when there isn't one. cmdk's own `Command.Dialog` gives its
            content only an `aria-label`, so it trips that warning on every
            open — which is why this composes the dialog by hand instead.
          */}
          <Dialog.Title className="sr-only">Command palette</Dialog.Title>
          <Dialog.Description className="sr-only">
            Search vendors, customers and pets, or jump to a page.
          </Dialog.Description>
          <Command
            label="Command palette"
            // With a term typed we want the API's matches, not cmdk's fuzzy
            // filter on top of them — double-filtering hides server hits whose
            // relevance the client can't see.
            shouldFilter={!searching}
            // cmdk's list navigation lives on the root, so the root has to own
            // the keydown handling — hence the loop here rather than on the input.
            loop
            className="overflow-hidden rounded-panel bg-popover shadow-overlay animate-scale-in"
          >
            <div className="flex items-center gap-2 border-b border-border px-3">
              <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
              <Command.Input
                value={term}
                onValueChange={set_term}
                placeholder="Search vendors, customers, pets or a microchip id…"
                className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                data-testid="palette-input"
              />
              {results.isFetching && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
              <kbd className="hidden shrink-0 rounded border border-border px-1.5 py-0.5 text-micro text-muted-foreground sm:block">
                Esc
              </kbd>
            </div>

            <Command.List className="thin-scroll max-h-[min(24rem,60vh)] overflow-y-auto p-1.5">
              <Command.Empty className="px-2 py-6 text-center text-data text-muted-foreground">
                {searching ? `Nothing matches “${debounced.trim()}”.` : 'Type to search.'}
              </Command.Empty>

              {searching && (
                <>
                  <HitGroup
                    heading="Vendors"
                    icon={Store}
                    hits={results.data?.vendors ?? []}
                    on_pick={go}
                  />
                  <HitGroup
                    heading="Users"
                    icon={Users}
                    hits={results.data?.users ?? []}
                    on_pick={go}
                  />
                  <HitGroup
                    heading="Pets"
                    icon={PawPrint}
                    hits={results.data?.pets ?? []}
                    on_pick={go}
                  />
                </>
              )}

              {!searching && (
                <HitGroup
                  heading="Recent bookings"
                  icon={CalendarCheck2}
                  hits={recent.data ?? []}
                  on_pick={go}
                />
              )}

              <Command.Group
                heading="Go to"
                className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-micro [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-muted-foreground"
              >
                {nav_items.map((item) => (
                  <Command.Item
                    key={item.to}
                    value={`${item.title} ${item.group} ${item.keywords ?? ''}`}
                    onSelect={() => go(item.to)}
                    className={item_class}
                    data-testid={`palette-nav-${item.to}`}
                  >
                    <item.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate">{item.title}</span>
                    <span className="text-micro text-muted-foreground">{item.group}</span>
                    <ArrowRight className="h-3.5 w-3.5 opacity-0 group-aria-selected/item:opacity-60" />
                  </Command.Item>
                ))}
              </Command.Group>

              <Command.Group
                heading="Appearance"
                className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-micro [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-muted-foreground"
              >
                <Command.Item
                  value="light theme appearance"
                  onSelect={() => {
                    set_theme('light');
                    set_open(false);
                  }}
                  className={item_class}
                >
                  <Sun className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1">Switch to light</span>
                </Command.Item>
                <Command.Item
                  value="dark theme appearance"
                  onSelect={() => {
                    set_theme('dark');
                    set_open(false);
                  }}
                  className={item_class}
                >
                  <Moon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1">Switch to dark</span>
                </Command.Item>
              </Command.Group>
            </Command.List>

            <div className="flex items-center justify-between border-t border-border px-3 py-1.5 text-micro text-muted-foreground">
              <span className="flex items-center gap-1">
                <CornerDownLeft className="h-3 w-3" /> to open
              </span>
              <span>↑↓ to navigate</span>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const item_class =
  'group/item flex cursor-pointer items-center gap-2 rounded-control px-2 py-1.5 text-sm outline-none aria-selected:bg-primary/10 aria-selected:text-foreground';

function HitGroup({
  heading,
  icon: Icon,
  hits,
  on_pick,
}: {
  heading: string;
  icon: typeof Store;
  hits: Hit[];
  on_pick: (to: string) => void;
}) {
  if (hits.length === 0) return null;
  return (
    <Command.Group
      heading={heading}
      className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-micro [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-muted-foreground"
    >
      {hits.map((hit) => (
        <Command.Item
          key={hit.id}
          value={`${hit.title} ${hit.subtitle} ${hit.id}`}
          onSelect={() => on_pick(hit.to)}
          className={item_class}
          data-testid={`palette-hit-${hit.id}`}
        >
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{hit.title}</span>
            {hit.subtitle && (
              <span className="block truncate text-micro text-muted-foreground">
                {hit.subtitle}
              </span>
            )}
          </span>
        </Command.Item>
      ))}
    </Command.Group>
  );
}
